/*
 * $Id:: AddressCard.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState, useEffect } from 'react';

import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { useSelector, useDispatch } from 'react-redux';

import { GLOBALS_SLICE_NAME } from '../../../../app/constants';
import { countries as countriesSlice, loading as loadingSlice } from '../../../../app/globalsSlice';
import inputsMaxChars from '../../../../app/inputsMaxChars';
import { ReactComponent as CheckedIcon } from '../../../../assets/images/check_box_24px.svg';
import { ReactComponent as UncheckedIcon } from '../../../../assets/images/check_box_outline_blank_24px.svg';
import { ReactComponent as DropDownArrow } from '../../../../assets/images/unfold_more_24px.svg';
import {isNumber, isEmail, freeOfInvalidChars} from '../../../../common/utils/service';
import Card from '../../../../components/Card';
import ErrorInput from '../../../../components/ErrorInput';
import { t } from '../../../../providers';
import { loadingAddress, accountLocked as accountLockedSlice, loadingAccountLockAddress as loadingAccountLockAddressSlice } from '../../accountSlice';

const useStyles = makeStyles((theme) => ({
    cardTitle: {
        fontSize: '14px',
        fontWeight: '600',
        color: theme.palette.text.outlined,
        marginTop: '10px',
    },
    fieldLabel: {
        color: theme.palette.text.label,
        fontSize: '10px',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: 'normal',
    },
    fieldValue: {
        color: theme.palette.text.value,
        fontSize: '12px',
        letterSpacing: 'normal',
        marginBottom: '10px',
    },
    linkValue: {
        color: theme.palette.text.linkBlue,
        fontSize: '12px',
        letterSpacing: 'normal',
        marginBottom: '10px',
        textDecoration: 'underline',
    },
    textInput: {
        height: '2rem',
        width: '9rem',
        borderRadius: '2px',
        marginTop: '5px',
        marginBottom: '10px',
        '@media (max-width:600px)': {
            width: '9.5rem',
        },
    },
    selectWidth: {
        width: '12rem',
    },
    selectSmallWidth: {
        width: '11rem',
    },
    checkboxLabel: {
        '& .MuiFormControlLabel-label': {
            fontSize: '14px',
        },
    },
    checkboxDisabled: {
        '& path': {
            fill: theme.palette.disabled.button,
        },
    },
    corrAddressTitle: {
        marginLeft: '-10px',
        marginBottom: '24px',
        marginTop: '20px',
    },
    inputError: {
        marginTop: '-5px',
        marginBottom: '5px',
    },
}));

const AddressCard = ({ initialValues, saveDetails, hasAdministratorRole, handleLockAccount, clearLockAccount, isBusinessAccount }) => {
    const classes = useStyles();
    const countries = useSelector(countriesSlice);
    const loading = useSelector(loadingAddress);
    const loadingCountries = useSelector(loadingSlice);
    const dispatch = useDispatch();
    const accountLocked = useSelector(accountLockedSlice);
    const loadingAccountLockAddress = useSelector(loadingAccountLockAddressSlice);

    const [readonly, setReadonly] = useState(true);
    const [legAddress, setLegAddress] = useState({});
    const [triggerInitialData, setTriggerInitialData] = useState(false); // trigger to get initial data
    const [corrAddress, setCorrAddress] = useState({});
    const [hasCorrAddress, setHasCorrAddress] = useState(!!initialValues.correspondentAddress);

    const getCountryName = (code, options) => {
        const country = options.find((c) => c.code === code) || {};
        return t(`countries.${country.value}`) || code;
    };

    const getAddressValues = (fields) =>
        Object.keys(fields).reduce((acc, cur) => (fields[cur].isDirty ? { ...acc, [cur]: fields[cur].value } : { ...acc }), {});

    const editCard = () => {
        handleLockAccount('Address');
    };

    const cancelCard = (event) => {
        event.preventDefault();

        setTriggerInitialData(!triggerInitialData);
        setReadonly(true);
    };

    const saveCard = (event) => {
        event.preventDefault();

        const newLegalAddress = getAddressValues(legAddress);
        const newCorrAddress = hasCorrAddress ? getAddressValues(corrAddress) : null;

        // validate legAddress for errors & required fields
        const hasErrorsLegAddress = Object.keys(legAddress).some((field) => !!legAddress[field].errorMessage);
        let legAddressUpdatedWithErrorsMessage = { ...legAddress };
        const hasRequiredValuesLegAddress = Object.fromEntries(
            Object.entries(legAddressUpdatedWithErrorsMessage).filter(([key, fieldObject]) => {
                let result = false;
                if (fieldObject.required && fieldObject.value === '') {
                    legAddressUpdatedWithErrorsMessage[key].errorMessage = 'required.field';
                    result = true;
                }
                return result;
            })
        );
        if (Object.keys(hasRequiredValuesLegAddress).length > 0) setLegAddress(legAddressUpdatedWithErrorsMessage);

        // validate corrAddress for errors & required fields
        let hasErrorsCorrAddress = true;
        let hasRequiredValuesCorrAddress = {};
        let corrAddressUpdatedWithErrorsMessage = { ...legAddress };

        if (hasCorrAddress) {
            hasErrorsCorrAddress = Object.keys(corrAddress).some((field) => !!corrAddress[field].errorMessage);
            corrAddressUpdatedWithErrorsMessage = { ...corrAddress };
            hasRequiredValuesCorrAddress = Object.fromEntries(
                Object.entries(corrAddressUpdatedWithErrorsMessage).filter(([key, fieldObject]) => {
                    let result = false;
                    if (fieldObject.required && fieldObject.value === '') {
                        corrAddressUpdatedWithErrorsMessage[key].errorMessage = 'required.field';
                        result = true;
                    }
                    return result;
                })
            );
            if (Object.keys(hasRequiredValuesCorrAddress).length > 0) setCorrAddress(corrAddressUpdatedWithErrorsMessage);
        }

        // set canProceed for legAddress
        let canProceed = !hasErrorsLegAddress && Object.keys(hasRequiredValuesLegAddress).length <= 0;
        // set canProceed for corrAddress, if enabled
        if (canProceed && hasCorrAddress) canProceed = !hasErrorsCorrAddress && Object.keys(hasRequiredValuesCorrAddress).length <= 0;

        if (canProceed) {
            const payload = { legalAddress: { ...newLegalAddress, addressType: 'LEGAL' } };
            if (newCorrAddress) payload.correspondentAddress = { ...newCorrAddress, addressType: 'CORRESPONDENT' };
            payload.personDetails = { corrAddress: !!newCorrAddress };

            saveDetails(payload, 'Address');
        }
    };

    const handleValueChange = (event, setter, address) => {
        const value = event.target.value;
        const keyName = event.target.name;
        const field = { ...address[keyName], value, isDirty: true };
        if (!field.maxChars || (field.maxChars && value.length <= field.maxChars)) {
            let errorMessage = null;
            if (field.required && !value) errorMessage = 'required.field';
            if (value) {
                if (keyName === 'corrTelephone' && !isNumber(value)) errorMessage = 'invalid.format';
                if (keyName === 'corrEmail' && !isEmail(value)) errorMessage = 'invalid.email.format';
                if (!freeOfInvalidChars(value)) errorMessage = 'invalid.characters.error';
            }
            field.errorMessage = errorMessage;

            const newAddressValue = { ...address, [keyName]: field };

            setter({ ...newAddressValue });
        }
    };

    const handleCheckChange = (event) => {
        setHasCorrAddress(event.target.checked);
    };

    const createAddressBlock = (values, setter, idPrefix) => {
        return (
            <React.Fragment>
                {Object.keys(values)
                    .filter((f) => !values[f].noIterrate)
                    .map((keyName, idx) => {
                        const { label, placeholder, value, options, element, isLinkType, required = false, errorMessage } = values[keyName];

                        const isCode = ['country'].includes(keyName);
                        let selectOptions = options;
                        const commonProps = {
                            id: `${idPrefix}_${idx}_${keyName}`,
                            placeholder: t(placeholder),
                            name: keyName,
                            onChange: (e) => handleValueChange(e, setter, values),
                            value: value || '',
                        };
                        const valueClass = !isLinkType || !value ? classes.fieldValue : classes.linkValue;
                        const diplayedValue = (isCode ? getCountryName(value, selectOptions || []) : value) || '-';
                        const inputOrDropdown =
                            element === 'Input' ? (
                                <Input
                                    {...commonProps}
                                    className={classes.textInput}
                                    type="text"
                                    error={!!errorMessage}
                                    fullWidth
                                    autoFocus={idx === 0}
                                />
                            ) : (
                                <Select
                                    {...commonProps}
                                    className={`${classes.textInput} ${classes.selectWidth}`}
                                    displayEmpty
                                    error={!!errorMessage}
                                    IconComponent={DropDownArrow}>
                                    <MenuItem value="" disabled>
                                        {t(placeholder)}
                                    </MenuItem>
                                    {(selectOptions || []).map((option, idx) => (
                                        <MenuItem key={`${option.key}_${idx}`} value={option.code}>
                                            {t(`countries.${option.value}`)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            );

                        return (
                            <div key={`${idPrefix}_${keyName}_${idx}`}>
                                { readonly || (idPrefix === 'legalAddress' && isBusinessAccount) ? (
                                    <React.Fragment>
                                        <Typography className={classes.fieldLabel}>
                                            {t(label)} {required && ' *'}
                                        </Typography>
                                        <Typography className={valueClass}>{diplayedValue}</Typography>
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        <InputLabel error={!!errorMessage} htmlFor={keyName} className={classes.fieldLabel}>
                                            {t(label)} {required && ' *'}
                                        </InputLabel>
                                        {inputOrDropdown}
                                        {!!errorMessage && <ErrorInput errorMessage={t(errorMessage)} classes={`${classes.inputError}`} />}
                                    </React.Fragment>
                                )}
                            </div>
                        );
                    })}
            </React.Fragment>
        );
    };

    useEffect(() => {
        if (!loadingAccountLockAddress && !accountLocked) {
            setReadonly(false);
            clearLockAccount();
        }
    }, [loadingAccountLockAddress]);

    useEffect(() => {
        if ((!countries || !countries.length) && !loadingCountries && !loadingAddress) dispatch({ type: `${GLOBALS_SLICE_NAME}/fetchCountries` });

        const { legalAddress, correspondentAddress } = initialValues || {};
        const lAddr = legalAddress || {};
        const cAddr = correspondentAddress || {};
        const newLegAddress = {
            streetName: {
                value: lAddr.streetName || '',
                placeholder: 'type.street.name',
                label: 'street.name',
                isDirty: false,
                element: 'Input',
                required: true,
                maxChars: inputsMaxChars.streetName,
            },
            city: {
                value: lAddr.city || '',
                placeholder: 'type.city',
                label: 'city',
                isDirty: false,
                element: 'Input',
                required: true,
                maxChars: inputsMaxChars.city,
            },
            postalCode: {
                value: lAddr.postalCode || '',
                placeholder: 'type.postal.code',
                label: 'postal.code',
                isDirty: false,
                element: 'Input',
                required: true,
                maxChars: inputsMaxChars.postalCode,
            },
            country: {
                value: lAddr.country || '',
                placeholder: 'select.country',
                label: 'country',
                isDirty: false,
                element: 'Select',
                options: countries,
                required: true,
            },
        };

        let finalLegAddress = { ...newLegAddress };

        let newCorrAddress = {
            streetName: {
                value: cAddr.streetName || '',
                placeholder: 'type.street.name',
                label: 'street.name',
                isDirty: false,
                element: 'Input',
                required: true,
                maxChars: inputsMaxChars.streetName,
            },
            city: {
                value: cAddr.city || '',
                placeholder: 'type.city',
                label: 'city',
                isDirty: false,
                element: 'Input',
                required: true,
                maxChars: inputsMaxChars.city,
            },
            postalCode: {
                value: cAddr.postalCode || '',
                placeholder: 'type.postal.code',
                label: 'postal.code',
                isDirty: false,
                element: 'Input',
                maxChars: inputsMaxChars.postalCode,
                required: true,
            },
            country: {
                value: cAddr.country || '',
                placeholder: 'select.country',
                label: 'country',
                isDirty: false,
                element: 'Select',
                options: countries,
                required: true,
            },
            corrTelephone: {
                value: cAddr.corrTelephone || '',
                placeholder: 'type.correspondence.telephone',
                label: 'correspondence.telephone',
                isDirty: false,
                element: 'Input',
                isLinkType: true,
                maxChars: inputsMaxChars.corrTelephone,
            },
            corrEmail: {
                value: cAddr.corrEmail || '',
                placeholder: 'type.correspondence.email',
                label: 'correspondence.email',
                isDirty: false,
                element: 'Input',
                required: true,
                isLinkType: true,
                maxChars: inputsMaxChars.corrEmail,
            },
        };

        let finalCorrAddress = { ...newCorrAddress };

        setLegAddress(finalLegAddress);
        setCorrAddress(finalCorrAddress);
        setHasCorrAddress(!!correspondentAddress);
        setReadonly(true);
    }, [initialValues, countries, dispatch, triggerInitialData]);

    const renderItems = (
        <React.Fragment>
            {createAddressBlock(legAddress, setLegAddress, 'legalAddress')}
            <Grid item>
                <FormControlLabel
                    className={classes.checkboxLabel}
                    control={
                        <Checkbox
                            checked={hasCorrAddress}
                            className={readonly ? classes.checkboxDisabled : ''}
                            name="hasCorrAddress"
                            onChange={handleCheckChange}
                            checkedIcon={<CheckedIcon />}
                            icon={<UncheckedIcon />}
                            disabled={readonly}
                        />
                    }
                    label={t('correspondence.address.checkbox.label')}
                />
            </Grid>
            {hasCorrAddress && (
                <React.Fragment>
                    <Grid justify="space-between" alignItems="center" container className={classes.corrAddressTitle}>
                        <Grid item>
                            <Typography className={classes.cardTitle}>{t('correspondence.address.title')}</Typography>
                        </Grid>
                    </Grid>
                    {createAddressBlock(corrAddress, setCorrAddress, 'corrAddress')}
                </React.Fragment>
            )}
        </React.Fragment>
    );

    return (
        <Card
            title={'legal.address.title'}
            canEdit={hasAdministratorRole}
            readonly={readonly}
            editCard={editCard}
            saveCard={saveCard}
            cancelCard={cancelCard}
            cardId={'profile_address_card'}>
            {loading || loadingAccountLockAddress ? <CircularProgress /> : renderItems}
        </Card>
    );
};

export default AddressCard;
